import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import styles from "./ResetPassword.module.css";
import logo from "../../assets/house-icon-red.png";
import bgImg from "../../assets/citystrip.png";
import { BASE_URL } from "../../App";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function ResetPassword() {
  const q = useQuery();
  const [email] = useState(q.get("email") || "");
  const [otp, setOtp] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const pwOk =
    pw.length >= 8 && /[A-Z]/.test(pw) && /[a-z]/.test(pw) && /[0-9]/.test(pw);

  async function onSubmit(e) {
    e.preventDefault();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setMsg({ type: "error", text: "Email missing or invalid." });
      return;
    }
    if (!/^\d{6}$/.test(otp)) {
      setMsg({ type: "error", text: "Enter the 6-digit code sent to your email." });
      return;
    }
    if (!pwOk) {
      setMsg({ type: "error", text: "Password must be ≥ 8 chars and include upper, lower, and a digit." });
      return;
    }
    if (pw !== pw2) {
      setMsg({ type: "error", text: "Passwords do not match." });
      return;
    }

    try {
      setSubmitting(true);
      setMsg({ type: "", text: "" });

      // IMPORTANT: this only calls reset.php (no new email is requested)
      const res = await fetch(`${BASE_URL}/forgot_password/reset.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp, new_password: pw }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || "Something went wrong");

      setMsg({ type: "success", text: "Password has been reset." });
      // (No redirect needed; keeps the message visible. Link to login is below.)
    } catch (err) {
      setMsg({ type: "error", text: err.message });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <img src={logo} alt="HomeMate logo" className={styles.headerLogo} />
          <span className={styles.headerBrand}>HomeMate</span>
        </div>
      </header>

      <section className={styles.hero}>
        <div className={styles.center}>
          <h1 className={styles.title}>
            Reset <span className={styles.titleEmph}>Password</span>
          </h1>
          <p className={styles.subtitle}>
            Enter the 6-digit code and choose a new password for <strong>{email || "your account"}</strong>.
          </p>

          <form onSubmit={onSubmit} className={styles.form}>
            <label className={styles.label}>6-digit code*</label>
            <input
              className={styles.input}
              inputMode="numeric"
              pattern="\d{6}"
              placeholder="------"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            />

            <label className={styles.label}>New password*</label>
            <input
              className={styles.input}
              type="password"
              placeholder="At least 8 chars, A-Z, a-z, 0-9"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
            />

            <label className={styles.label}>Confirm new password*</label>
            <input
              className={styles.input}
              type="password"
              placeholder="Re-enter new password"
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
            />

            <button className={styles.primaryBtn} disabled={submitting}>
              {submitting ? "Resetting..." : "Reset Password"}
            </button>
          </form>

          {msg.text && (
            <div
              className={
                msg.type === "success" ? styles.alertSuccess : styles.alertError
              }
              role="status"
            >
              {msg.text}
            </div>
          )}
        </div>

        <div
          className={styles.cityStrip}
          style={{ backgroundImage: `url(${bgImg})` }}
          aria-hidden
        />
      </section>

      <div className={styles.footerLinks}>
        <Link to="/login" className={styles.linkDark}>Back to Login</Link>
      </div>
    </div>
  );
}
